/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Table } from 'src/core/components/Table';
import { SortKeyType } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { QnaAddSourceModalCommandBar } from 'src/qnaMaker/appShell/qnaMakerManageSourcesPage/QnaMakerAddSourceModalCommandBar';

export type QnaFileSourceDto = { displayName: string; file: File; id: string; isUnstructured: boolean };
export type QnaAddFileSourceModalProps = BasicModalProps<QnaFileSourceDto[]>;

const messages = defineMessages({
    modalTitle: {
        id: 'qnaMaker.qnaAddFileSourceModal.modalTitle',
        defaultMessage: 'Add files'
    },
    modalDescription: {
        id: 'qnaMaker.qnaAddFileSourceModal.modalDescription',
        defaultMessage:
            'You can add up to 10 files at a time. Classification of file structure is auto-detected by default, but you can set your own file structure classification if preferred.'
    },
    addAll: {
        id: 'qnaMaker.qnaAddFileSourceModal.addAll',
        defaultMessage: 'Add all'
    },
    sourceName: {
        id: 'qnaMaker.qnaAddFileSourceModal.sourceName',
        defaultMessage: 'Source name'
    },
    fileName: {
        id: 'qnaMaker.qnaAddFileSourceModal.fileName',
        defaultMessage: 'File name'
    },
    sourceUrl: {
        id: 'qnaMaker.qnaAddFileSourceModal.sourceUrl',
        defaultMessage: 'URL'
    },
    classifyStructure: {
        id: 'qnaMaker.qnaAddFileSourceModal.classifyStructure',
        defaultMessage: 'Classify file structure'
    },
    browse: {
        id: 'qnaMaker.qnaAddFileSourceModal.browse',
        defaultMessage: 'Browse for files'
    },
    autoDetect: {
        id: 'qnaMaker.qnaAddFileSourceModal.autoDetect',
        defaultMessage: 'Auto-detect'
    },
    unstructured: {
        id: 'qnaMaker.qnaAddFileSourceModal.unstructured',
        defaultMessage: 'Unstructured'
    }
});

const editableItemDefaultValue: QnaFileSourceDto = { id: '', displayName: '', file: null, isUnstructured: false };

export const QnaAddFileSourceModal = withModalMounter((props: QnaAddFileSourceModalProps) => {
    const { onSubmit } = props;
    const { localize } = useLocalization();
    const [sortKey, setSortKey] = React.useState<SortKeyType<QnaFileSourceDto>>({ key: 'displayName', order: 'desc' });

    const [sourceItems, setSourceItems] = React.useState<QnaFileSourceDto[]>([]);
    const [newItemId] = React.useState(getId('newItem'));

    const [editableItem, setEditableItem] = useUpdateState<QnaFileSourceDto>(editableItemDefaultValue);

    const { selection, selectedItems } = useSelection<QnaFileSourceDto>();
    const isModalValid = sourceItems.length && sourceItems.every(i => i.displayName.trim() && i.file);

    React.useEffect(() => {
        setSourceItems(items => orderBy(items, [createKeyedStringNormalizer('displayName')], [sortKey.order]));
    }, [sortKey]);

    const handleSubmitNewItem = () => {
        if (editableItem.displayName) {
            setSourceItems(sources =>
                sources.map(item => {
                    if (item.id === editableItem.id) {
                        return { ...editableItem, displayName: editableItem.displayName, id: getId() };
                    }

                    return item;
                })
            );
            setEditableItem(editableItemDefaultValue);
            selection.setAllSelected(false);
        }
    };

    const handleAddSource = () => {
        const newItem = { ...editableItemDefaultValue, id: newItemId };
        setSourceItems(sources => [newItem, ...sources]);
        setEditableItem(newItem);
    };

    const handleDeleteSource = () => {
        setSourceItems(sources => sources.filter(item => !selectedItems.some(i => i.id === item.id)));
        selection.setAllSelected(false);
    };

    const handleRenameSource = () => {
        setEditableItem({ ...selectedItems[0] });
    };

    const handleBrowsingFiles = (e: React.ChangeEvent<HTMLInputElement>, itemId: string): void => {
        const { files } = e.target as HTMLInputElement;
        if (files) {
            setEditableItem({ ...editableItem, file: files[0] });
            setSourceItems(sources =>
                sources.map(item => {
                    if (item.id === itemId) {
                        return { ...item, file: files[0] };
                    }
                    return item;
                })
            );
        }
        selection.setAllSelected(false);
    };

    const handleClassifyFileStructure = (itemId: string, isUnstructured: boolean) => {
        setSourceItems(sources =>
            sources.map(item => {
                if (item.id === itemId) {
                    return { ...item, isUnstructured };
                }
                return item;
            })
        );
        selection.setAllSelected(false);
    };

    const onSortColumn = (col: IColumn) => setSortKey({ key: 'displayName', order: col.isSortedDescending ? 'asc' : 'desc' });

    const columns: IColumn[] = [
        {
            key: 'displayName',
            minWidth: 150,
            fieldName: 'displayName',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.sourceName),
            isSorted: sortKey && sortKey.key === 'displayName',
            isSortedDescending: sortKey && sortKey.key === 'displayName' && sortKey.order === 'desc',
            onRender: (item: QnaFileSourceDto) =>
                editableItem.id === item.id ? (
                    <LuisTextField
                        placeholder={localize(messages.sourceName)}
                        onChange={(_, displayName) => {
                            setEditableItem({ displayName });
                        }}
                        autoFocus
                        value={editableItem.displayName}
                        onBlur={handleSubmitNewItem}
                    />
                ) : (
                    <TruncatedText title={item.displayName}>{item.displayName}</TruncatedText>
                )
        },
        {
            key: 'fileName',
            minWidth: 200,
            fieldName: 'fileName',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.fileName),
            onRender: (item: QnaFileSourceDto) =>
                item.file ? (
                    item.file.name
                ) : (
                    <input
                        type="file"
                        multiple={false}
                        accept=".tsv, application/pdf, text/plain, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/tab-separated-values"
                        onChange={e => handleBrowsingFiles(e, item.id)}
                    />
                )
        },
        {
            key: 'classifyStructure',
            minWidth: 200,
            fieldName: 'classifyStructure',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.classifyStructure),
            onRender: (item: QnaFileSourceDto) => (
                <LuisDropdown
                    options={[
                        { key: 'autoDetect', text: localize(messages.autoDetect) },
                        { key: 'unstructured', text: localize(messages.unstructured) }
                    ]}
                    isSearchEnabled={false}
                    selectedKey={item.isUnstructured ? 'unstructured' : 'autoDetect'}
                    onChange={(_, i) => handleClassifyFileStructure(item.id, i.key === 'unstructured')}
                />
            )
        }
    ];

    return (
        <BasicModal
            {...props}
            minWidth={950}
            valid={isModalValid}
            onSubmit={() => onSubmit(sourceItems)}
            doneButtonText={localize(messages.addAll)}
            dialogContentProps={{
                ...props.dialogContentProps,
                title: localize(messages.modalTitle),
                subText: localize(messages.modalDescription)
            }}>
            <Stack styles={{ root: { minHeight: 250 } }} verticalFill>
                <QnaAddSourceModalCommandBar
                    sourceKind="file"
                    isAddDisabled={Boolean(editableItem.id)}
                    selectedItems={selectedItems}
                    onAddSources={handleAddSource}
                    onDeleteSource={handleDeleteSource}
                    onRenameSource={handleRenameSource}
                />
                <Table<QnaFileSourceDto> items={sourceItems} columns={columns} selection={selection} onSortColumn={onSortColumn} />
            </Stack>
        </BasicModal>
    );
});
